package org.zq.astrock_ai.quant

internal interface QuantStrategy {
    val info: StrategyInfo
    fun signal(bars: List<QuantBar>, params: Map<String, String> = emptyMap()): QuantSignal
    fun sides(bars: List<QuantBar>, params: Map<String, String> = emptyMap()): List<QuantSide>
}

private fun round1(value: Double): String {
    val scaled = kotlin.math.round(value * 10.0) / 10.0
    val asLong = scaled.toLong()
    return if (scaled == asLong.toDouble()) asLong.toString() else scaled.toString()
}

internal class DualMaStrategy : QuantStrategy {
    override val info = StrategyInfo(
        id = "dual_ma",
        name = "双均线交叉",
        description = "短期均线上穿长期均线买入，下穿卖出。默认 MA5 / MA20。",
        defaultParams = mapOf("fast" to "5", "slow" to "20"),
    )

    override fun sides(bars: List<QuantBar>, params: Map<String, String>): List<QuantSide> {
        val fastN = params["fast"]?.toIntOrNull() ?: 5
        val slowN = params["slow"]?.toIntOrNull() ?: 20
        val closes = bars.map { it.close }
        val fast = Indicators.sma(closes, fastN)
        val slow = Indicators.sma(closes, slowN)
        return bars.indices.map { i ->
            val f = fast[i]
            val s = slow[i]
            val pf = fast.getOrNull(i - 1)
            val ps = slow.getOrNull(i - 1)
            when {
                f == null || s == null || pf == null || ps == null -> QuantSide.HOLD
                pf <= ps && f > s -> QuantSide.BUY
                pf >= ps && f < s -> QuantSide.SELL
                else -> QuantSide.HOLD
            }
        }
    }

    override fun signal(bars: List<QuantBar>, params: Map<String, String>): QuantSignal {
        val merged = info.defaultParams + params
        val sides = sides(bars, merged)
        val last = bars.lastOrNull()
        val side = sides.lastOrNull() ?: QuantSide.HOLD
        val fastN = merged["fast"] ?: "5"
        val slowN = merged["slow"] ?: "20"
        val reason = when (side) {
            QuantSide.BUY -> "MA$fastN 上穿 MA$slowN，出现金叉"
            QuantSide.SELL -> "MA$fastN 下穿 MA$slowN，出现死叉"
            QuantSide.HOLD -> "均线未出现新交叉，维持观望"
        }
        return QuantSignal(
            strategyId = info.id,
            strategyName = info.name,
            side = side.name,
            reason = reason,
            asOf = last?.date ?: "-",
            price = last?.close ?: 0.0,
            params = merged,
        )
    }
}

internal class BreakoutStrategy : QuantStrategy {
    override val info = StrategyInfo(
        id = "breakout",
        name = "区间突破",
        description = "收盘突破近 N 日高点买入，跌破近 N 日低点卖出。默认回看 20 日。",
        defaultParams = mapOf("lookback" to "20"),
    )

    override fun sides(bars: List<QuantBar>, params: Map<String, String>): List<QuantSide> {
        val n = params["lookback"]?.toIntOrNull() ?: 20
        val highs = bars.map { it.high }
        val lows = bars.map { it.low }
        return bars.indices.map { i ->
            if (i < n) return@map QuantSide.HOLD
            val priorHigh = Indicators.highest(highs, n, i - 1)
            val priorLow = Indicators.lowest(lows, n, i - 1)
            val close = bars[i].close
            when {
                close > priorHigh -> QuantSide.BUY
                close < priorLow -> QuantSide.SELL
                else -> QuantSide.HOLD
            }
        }
    }

    override fun signal(bars: List<QuantBar>, params: Map<String, String>): QuantSignal {
        val merged = info.defaultParams + params
        val sides = sides(bars, merged)
        val last = bars.lastOrNull()
        val side = sides.lastOrNull() ?: QuantSide.HOLD
        val n = merged["lookback"] ?: "20"
        val reason = when (side) {
            QuantSide.BUY -> "收盘突破近 ${n} 日高点"
            QuantSide.SELL -> "收盘跌破近 ${n} 日低点"
            QuantSide.HOLD -> "价格仍在近 ${n} 日区间内震荡"
        }
        return QuantSignal(
            strategyId = info.id,
            strategyName = info.name,
            side = side.name,
            reason = reason,
            asOf = last?.date ?: "-",
            price = last?.close ?: 0.0,
            params = merged,
        )
    }
}

internal class RsiReversalStrategy : QuantStrategy {
    override val info = StrategyInfo(
        id = "rsi_reversal",
        name = "RSI 超买超卖",
        description = "RSI 上穿超卖线买入，下穿超买线卖出。默认周期 14，超卖 30，超买 70。",
        defaultParams = mapOf("period" to "14", "oversold" to "30", "overbought" to "70"),
    )

    override fun sides(bars: List<QuantBar>, params: Map<String, String>): List<QuantSide> {
        val period = params["period"]?.toIntOrNull() ?: 14
        val oversold = params["oversold"]?.toDoubleOrNull() ?: 30.0
        val overbought = params["overbought"]?.toDoubleOrNull() ?: 70.0
        val rsi = Indicators.rsi(bars.map { it.close }, period)
        return bars.indices.map { i ->
            val cur = rsi[i] ?: return@map QuantSide.HOLD
            val prev = rsi.getOrNull(i - 1) ?: return@map QuantSide.HOLD
            when {
                prev <= oversold && cur > oversold -> QuantSide.BUY
                prev >= overbought && cur < overbought -> QuantSide.SELL
                else -> QuantSide.HOLD
            }
        }
    }

    override fun signal(bars: List<QuantBar>, params: Map<String, String>): QuantSignal {
        val merged = info.defaultParams + params
        val sides = sides(bars, merged)
        val last = bars.lastOrNull()
        val side = sides.lastOrNull() ?: QuantSide.HOLD
        val period = merged["period"] ?: "14"
        val oversold = merged["oversold"] ?: "30"
        val overbought = merged["overbought"] ?: "70"
        val rsiNow = Indicators.rsi(bars.map { it.close }, period.toIntOrNull() ?: 14).lastOrNull()
        val rsiText = rsiNow?.let { round1(it) } ?: "-"
        val reason = when (side) {
            QuantSide.BUY -> "RSI($period)=$rsiText 上穿超卖线 $oversold"
            QuantSide.SELL -> "RSI($period)=$rsiText 下穿超买线 $overbought"
            QuantSide.HOLD -> "RSI($period)=$rsiText，未触发超买/超卖反转"
        }
        return QuantSignal(
            strategyId = info.id,
            strategyName = info.name,
            side = side.name,
            reason = reason,
            asOf = last?.date ?: "-",
            price = last?.close ?: 0.0,
            params = merged,
        )
    }
}
